import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";

import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../../consts";
import { calculateGDD, getTodayDate } from "./utils";
import { gatherExcludedDatesForGreenhouse } from "./forecastRules";
import {
  endpointGrowthIsNegative,
  seriesMinMax,
} from "../../../../utils/positiveTrendCorrection";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

export const MAX_ROW_TASKS_CONCURRENCY = 40;

export const mapWithConcurrency = async (
  items,
  mapper,
  limit,
  { swallowErrors = true } = {},
) => {
  if (!items?.length) return [];
  const out = new Array(items.length);
  let i = 0;
  const workers = Array(Math.min(limit, items.length))
    .fill(0)
    .map(async () => {
      while (true) {
        const idx = i++;
        if (idx >= items.length) break;
        try {
          out[idx] = await mapper(items[idx], idx);
        } catch (error) {
          if (!swallowErrors) throw error;
          out[idx] = undefined;
        }
      }
    });
  await Promise.all(workers);
  return out.filter((item) => item !== undefined);
};

const createEmptyForecastRow = ({
  greenhouse,
  row,
  harvestDate,
  yieldValue,
  weight,
  includeHarvestState,
  isHarvestedRecord,
  isDiscarded,
}) => {
  const forecastRow = {
    plotId: row.id,
    greenhouseId: greenhouse.id,
    plotName: row.name,
    lastData: {
      date: "",
      averagePercentile80_95: "-",
      averageLength: "-",
    },
    day1: {
      date: "",
      averagePercentile80_95: "-",
      averageLength: "-",
    },
    day2: {
      date: "",
      averagePercentile80_95: "-",
      averageLength: "",
    },
    day3: {
      date: "",
      averagePercentile80_95: "-",
      averageLength: "",
    },
    day4: {
      date: "",
      averagePercentile80_95: "-",
      averageLength: "",
    },
    day5: {
      date: "",
      averagePercentile80_95: "-",
      averageLength: "",
    },
    harvestDate,
    yield: yieldValue,
    weight,
  };

  if (includeHarvestState) {
    forecastRow.isHarvested = isHarvestedRecord;
    forecastRow.isDiscarded = isDiscarded;
  }

  return forecastRow;
};

const normalizeForecastDateRange = (sortedFilteredDatesForForecast) => {
  const sliceStart = dayjs(
    sortedFilteredDatesForForecast[0].date,
    UI_DATE_FORMAT,
  ).format(BACKEND_DATE_FORMAT);
  const sliceEnd = dayjs(
    sortedFilteredDatesForForecast[
      sortedFilteredDatesForForecast.length - 1
    ].date,
    UI_DATE_FORMAT,
  ).format(BACKEND_DATE_FORMAT);

  return { sliceStart, sliceEnd };
};

export const prepareGreenhouseForecastContext = ({
  greenhouse,
  excelRows,
  allRowGraph,
  harvestedMap,
  extractTableDataFromRawData,
  includeHarvestState = false,
}) => {
  const ghName = greenhouse.name;
  let allRowsHarvested = true;
  const rowDataMap = new Map();

  for (const row of greenhouse.plots) {
    try {
      let firstAvgDate = null;
      const rowEntries = allRowGraph[ghName]?.[row.name];
      if (Array.isArray(rowEntries) && rowEntries.length) {
        for (const entry of rowEntries) {
          const d = entry?.date ? dayjs(entry.date, BACKEND_DATE_FORMAT) : null;
          if (!d || !d.isValid()) continue;
          if (!firstAvgDate || d.isBefore(firstAvgDate)) {
            firstAvgDate = d;
          }
        }
      }

      const tableData =
        extractTableDataFromRawData(
          row.name,
          excelRows[ghName] || [],
          firstAvgDate,
        ) || {};

      const harvestedKey = `${greenhouse.id}-${row.id}`;
      const harvestedPlot = harvestedMap.get(harvestedKey);
      const isHarvestedRecord = Boolean(harvestedPlot?.harvestedDate);
      if (harvestedPlot?.harvestedDate) {
        tableData.harvestDate = harvestedPlot.harvestedDate;
        tableData.yield = harvestedPlot?.totalYield ?? 0;
        tableData.weight = harvestedPlot?.weight ?? 0;
      } else if (tableData?.harvestDate) {
        tableData.yield = 0;
        tableData.weight = 0;
      } else {
        tableData.yield = null;
        tableData.weight = null;
      }

      if (
        !tableData?.harvestDate ||
        !dayjs(tableData.harvestDate).isBefore(dayjs())
      ) {
        allRowsHarvested = false;
      }

      rowDataMap.set(row.name, {
        tableData,
        rowRef: row,
        ...(includeHarvestState ? { isHarvestedRecord } : {}),
      });
    } catch (error) {
      console.error(`Preprocessing error in ${ghName}/${row.name}:`, error);
      allRowsHarvested = false;
    }
  }

  return { rowDataMap, allRowsHarvested };
};

export const buildGreenhouseForecastRows = async ({
  greenhouse,
  rowDataMap,
  allRowGraph,
  filterRules,
  latLong,
  gddBaseTemperature,
  getAverageRowLevelData,
  getSortedFilteredDatesRange,
  memoizedCalcDiffRange,
  getTempSliceOrFetch,
  getForecastLeafSize,
  maxRowTasksConcurrency,
  forecastRowConcurrency = 1,
  passTempFetcherToForecast = false,
  failOnForecastRowError = false,
  precomputedForecastInputs,
  includeHarvestState = false,
  correctNegativeTrend = false,
  shouldBuildForecastForRow = () => true,
}) => {
  const ghName = greenhouse.name;
  const avgRows = await mapWithConcurrency(
    greenhouse.plots,
    async (row) => {
      const rowLevelData = allRowGraph[ghName]?.[row.name] || [];
      const avg = await getAverageRowLevelData(ghName, row.name, rowLevelData);
      return { rowName: row.name, avg };
    },
    maxRowTasksConcurrency,
  );

  const greenhouseLevelAverageData = {};
  for (const item of avgRows) {
    if (!item?.rowName) continue;
    greenhouseLevelAverageData[item.rowName] = item.avg;
  }

  const greenhouseExcludedDates = gatherExcludedDatesForGreenhouse(
    filterRules,
    ghName,
    rowDataMap,
    greenhouseLevelAverageData,
  );

  const buildRowForecast = async (row) => {
    const rowData = rowDataMap.get(row.name);
    const tableData = rowData?.tableData || {};
    const isHarvestedRecord = includeHarvestState
      ? Boolean(rowData?.isHarvestedRecord)
      : undefined;
    const isDiscarded = Number(tableData?.yield) === -1;
    const rowLevelAverageData = greenhouseLevelAverageData[row.name];

    const harvestDate = tableData?.harvestDate || "";
    const plantingDate = tableData?.plantingDate
      ? dayjs(tableData.plantingDate)
      : null;

    const lastDate = harvestDate
      ? dayjs(harvestDate).isBefore(dayjs())
        ? dayjs(harvestDate)
        : getTodayDate(latLong)
      : getTodayDate(latLong);

    const excludedDates = greenhouse?.metadata?.FORECAST_EXCLUDE_DATE || [];
    const finalExcludedDates = [
      ...new Set([...excludedDates, ...greenhouseExcludedDates]),
    ];

    const isRowHarvested =
      tableData?.harvestDate && dayjs(tableData.harvestDate).isBefore(dayjs());

    const emptyForecastRow = () =>
      createEmptyForecastRow({
        greenhouse,
        row,
        harvestDate,
        yieldValue: tableData.yield,
        weight: tableData.weight,
        includeHarvestState,
        isHarvestedRecord,
        isDiscarded,
      });

    if (!shouldBuildForecastForRow({ row, tableData, isHarvestedRecord })) {
      return emptyForecastRow();
    }

    let sortedFilteredDatesForForecast;
    let sliceStart;
    let sliceEnd;
    const precomputedInput = precomputedForecastInputs?.get(row.name);
    if (precomputedInput) {
      sortedFilteredDatesForForecast =
        precomputedInput.sortedFilteredDatesForForecast;
      sliceStart = precomputedInput.sliceStart;
      sliceEnd = precomputedInput.sliceEnd;
    } else {
      sortedFilteredDatesForForecast = getSortedFilteredDatesRange(
        [plantingDate, lastDate],
        rowLevelAverageData,
        finalExcludedDates,
        !isRowHarvested,
      );
    }

    if (
      !sortedFilteredDatesForForecast ||
      sortedFilteredDatesForForecast.length === 0
    ) {
      return emptyForecastRow();
    }

    const totalAvgLeafSize = memoizedCalcDiffRange(
      sortedFilteredDatesForForecast,
    );
    const totalAvgLeafSize80_95 = memoizedCalcDiffRange(
      sortedFilteredDatesForForecast,
      "averagePercentile80_95",
    );

    if (!sliceStart || !sliceEnd) {
      ({ sliceStart, sliceEnd } = normalizeForecastDateRange(
        sortedFilteredDatesForForecast,
      ));
    }

    const filteredTempData = await getTempSliceOrFetch(sliceStart, sliceEnd);
    const gddResult = calculateGDD(filteredTempData, gddBaseTemperature);
    const safeGddResult = Number.isFinite(gddResult) ? gddResult : 0;

    const lastMeasurementStr =
      sortedFilteredDatesForForecast[
        sortedFilteredDatesForForecast.length - 1
      ].date;
    const lastMeasurementDate = dayjs(lastMeasurementStr, UI_DATE_FORMAT);

    const forecastArgs = [
      lastMeasurementDate,
      totalAvgLeafSize,
      totalAvgLeafSize80_95,
      safeGddResult,
    ];
    if (passTempFetcherToForecast) {
      forecastArgs.push(getTempSliceOrFetch);
    }

    const forecastData = await getForecastLeafSize(...forecastArgs);

    const [
      day1 = { date: "", averagePercentile80_95: 0, averageLength: 0 },
      day2 = { date: "", averagePercentile80_95: 0, averageLength: 0 },
      day3 = { date: "", averagePercentile80_95: 0, averageLength: 0 },
      day4 = { date: "", averagePercentile80_95: 0, averageLength: 0 },
      day5 = { date: "", averagePercentile80_95: 0, averageLength: 0 },
    ] = Array.isArray(forecastData) ? forecastData : [];

    const lastRec =
      sortedFilteredDatesForForecast[
        sortedFilteredDatesForForecast.length - 1
      ];

    // OF-129: when enabled, compute a positive-trend-corrected forecast for
    // DISPLAY only (reorder the visible datapoints ascending => growth is
    // max-min and "current" becomes the max value). Attached as `corrected`,
    // which the persistence/raw-export paths never read, so the backend and
    // saved forecasts stay untouched.
    let corrected;
    if (correctNegativeTrend) {
      const avgValues = sortedFilteredDatesForForecast.map(
        (d) => d.averageLength,
      );
      const p95Values = sortedFilteredDatesForForecast.map(
        (d) => d.averagePercentile80_95,
      );
      const avgNeg = endpointGrowthIsNegative(avgValues);
      const p95Neg = endpointGrowthIsNegative(p95Values);
      if (avgNeg || p95Neg) {
        const avgExt = seriesMinMax(avgValues);
        const p95Ext = seriesMinMax(p95Values);
        const correctedArgs = [
          lastMeasurementDate,
          avgNeg ? avgExt.max - avgExt.min : totalAvgLeafSize,
          p95Neg ? p95Ext.max - p95Ext.min : totalAvgLeafSize80_95,
          safeGddResult,
        ];
        if (passTempFetcherToForecast) correctedArgs.push(getTempSliceOrFetch);
        const correctedForecastData = await getForecastLeafSize(
          ...correctedArgs,
        );
        const [cd1, cd2, cd3, cd4, cd5] = Array.isArray(correctedForecastData)
          ? correctedForecastData
          : [];
        corrected = {
          lastData: {
            ...lastRec,
            averageLength: avgNeg ? avgExt.max : lastRec.averageLength,
            averagePercentile80_95: p95Neg
              ? p95Ext.max
              : lastRec.averagePercentile80_95,
          },
          day1: cd1 ?? day1,
          day2: cd2 ?? day2,
          day3: cd3 ?? day3,
          day4: cd4 ?? day4,
          day5: cd5 ?? day5,
        };
      }
    }

    const forecastRow = {
      plotId: row.id,
      greenhouseId: greenhouse.id,
      plotName: row.name,
      lastData: lastRec,
      day1,
      day2,
      day3,
      day4,
      day5,
      harvestDate,
      yield: tableData.yield,
      weight: tableData.weight,
      ...(corrected ? { corrected } : {}),
    };

    if (includeHarvestState) {
      forecastRow.isHarvested = isHarvestedRecord;
      forecastRow.isDiscarded = isDiscarded;
    }

    return forecastRow;
  };

  if (forecastRowConcurrency > 1) {
    return mapWithConcurrency(
      greenhouse.plots,
      buildRowForecast,
      forecastRowConcurrency,
      { swallowErrors: !failOnForecastRowError },
    );
  }

  const rowForecasts = [];
  for (const row of greenhouse.plots) {
    rowForecasts.push(await buildRowForecast(row));
  }

  return rowForecasts;
};

export const prepareForecastInputsForGreenhouse = async ({
  greenhouse,
  rowDataMap,
  allRowGraph,
  filterRules,
  latLong,
  getAverageRowLevelData,
  getSortedFilteredDatesRange,
  maxRowTasksConcurrency,
  shouldBuildForecastForRow = () => true,
}) => {
  const ghName = greenhouse.name;
  const avgRows = await mapWithConcurrency(
    greenhouse.plots,
    async (row) => {
      const rowLevelData = allRowGraph[ghName]?.[row.name] || [];
      const avg = await getAverageRowLevelData(ghName, row.name, rowLevelData);
      return { rowName: row.name, avg };
    },
    maxRowTasksConcurrency,
    { swallowErrors: false },
  );

  const greenhouseLevelAverageData = {};
  for (const item of avgRows) {
    if (!item?.rowName) continue;
    greenhouseLevelAverageData[item.rowName] = item.avg;
  }

  const greenhouseExcludedDates = gatherExcludedDatesForGreenhouse(
    filterRules,
    ghName,
    rowDataMap,
    greenhouseLevelAverageData,
  );

  const forecastInputs = new Map();

  for (const row of greenhouse.plots) {
    const rowData = rowDataMap.get(row.name);
    const tableData = rowData?.tableData || {};
    const isHarvestedRecord = Boolean(rowData?.isHarvestedRecord);
    const rowLevelAverageData = greenhouseLevelAverageData[row.name];
    const harvestDate = tableData?.harvestDate || "";
    const plantingDate = tableData?.plantingDate
      ? dayjs(tableData.plantingDate)
      : null;
    const lastDate = harvestDate
      ? dayjs(harvestDate).isBefore(dayjs())
        ? dayjs(harvestDate)
        : getTodayDate(latLong)
      : getTodayDate(latLong);
    const excludedDates = greenhouse?.metadata?.FORECAST_EXCLUDE_DATE || [];
    const finalExcludedDates = [
      ...new Set([...excludedDates, ...greenhouseExcludedDates]),
    ];
    const isRowHarvested =
      tableData?.harvestDate && dayjs(tableData.harvestDate).isBefore(dayjs());

    if (!shouldBuildForecastForRow({ row, tableData, isHarvestedRecord })) {
      continue;
    }

    const sortedFilteredDatesForForecast = getSortedFilteredDatesRange(
      [plantingDate, lastDate],
      rowLevelAverageData,
      finalExcludedDates,
      !isRowHarvested,
    );

    if (
      !sortedFilteredDatesForForecast ||
      sortedFilteredDatesForForecast.length === 0
    ) {
      continue;
    }

    forecastInputs.set(row.name, {
      sortedFilteredDatesForForecast,
      ...normalizeForecastDateRange(sortedFilteredDatesForForecast),
    });
  }

  return forecastInputs;
};
